import { useId, useState, type FormEvent, type Ref } from "react";
import { Plus, MessageSquare, Wrench, Heart, BookOpen, Briefcase, LibraryBig, FolderKanban, Pencil, X, PanelLeftClose } from "lucide-react";
import { Logo } from "./Logo";
import { NewSessionDialog, type NewSessionOpts } from "./NewSessionDialog";
import { SessionItem } from "./SessionItem";
import { KeepsakesList } from "./KeepsakesList";
import { useI18n } from "../i18n";
import type { TranslationKey } from "../i18n/types";
import type { Project, Session } from "../auth/types";
import type { KeepsakedMessage } from "../hooks/useKeepsakes";
import { FALLBACK_COMPANION, type CompanionDescriptor } from "../brain/persona";
import { setPendingScheduleOrigin, settingsHash } from "./settings/useSettingsSurface";

interface SidebarProps {
  onNewSession: (opts: NewSessionOpts) => void;
  sessions: Session[];
  archivedSessions?: Session[];
  activeSessionId: string | null;
  onSwitchSession: (sessionId: string) => void;
  onDeleteSession: (sessionId: string) => void;
  onRenameSession: (sessionId: string, label: string) => void;
  onTogglePin: (sessionId: string) => void;
  onArchiveSession?: (sessionId: string) => void;
  onRestoreSession?: (sessionId: string) => void;
  projects?: Project[];
  projectsLoading?: boolean;
  projectsError?: string | null;
  onCreateProject?: (name: string, identity: string) => Promise<Project | null>;
  onRenameProject?: (projectId: string, name: string) => Promise<boolean>;
  onAssignSessionProject?: (sessionId: string, project: Project | null) => Promise<boolean>;
  pendingProjectMoveSessionIds?: ReadonlySet<string>;
  onKeepsakeSelect?: (messageId: string, sessionId: string) => void;
  /** Opens the full cross-session, cross-identity keepsakes gallery. */
  onOpenKeepsakesGallery?: () => void;
  onOpenLibrary?: () => void;
  allKeepsakes?: KeepsakedMessage[];
  className?: string;
  /** PATH B SLICE 4 — passed through to NewSessionDialog's companion picker. */
  companions?: CompanionDescriptor[] | null;
  /** WR-W S1 — desktop (>=769px) sidebar collapse. Both undefined when the
   *  caller doesn't offer collapse (e.g. a bare test render) — the header
   *  toggle only renders when `onToggleCollapsed` is provided. */
  collapsed?: boolean;
  onToggleCollapsed?: () => void;
  /** AppShell's focus-rescue target on expand — attached to the header
   *  toggle button so it can be re-focused from outside this component. */
  collapseToggleRef?: Ref<HTMLButtonElement>;
  /** Session-bound wakes, slice 2′ — closes the mobile sidebar overlay before
   *  navigating to Settings ›Schedules, same overlay-exclusivity convention
   *  as AppShell's own openSettings/openLibrary. Absent in bare test renders. */
  onCloseSidebar?: () => void;
}

type FilterType = "all" | "personal" | "writing" | "office" | "building";
type ShelfView = "recent" | "projects" | "archived";

export interface SessionDateGroup {
  id: "pinned" | "today" | "yesterday" | "previous-7-days" | "earlier";
  label: "Pinned" | "Today" | "Yesterday" | "Previous 7 Days" | "Earlier";
  sessions: Session[];
}

/** Calendar shelving uses the browser's local midnight boundaries, including DST. */
export function groupSessionsByLocalDate(sessions: Session[], now = new Date()): SessionDateGroup[] {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1).getTime();
  const previousSevenDays = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7).getTime();
  const sorted = sessions.slice().sort(
    (a, b) => new Date(b.last_activity).getTime() - new Date(a.last_activity).getTime(),
  );
  const buckets: SessionDateGroup[] = [
    { id: "pinned", label: "Pinned", sessions: [] },
    { id: "today", label: "Today", sessions: [] },
    { id: "yesterday", label: "Yesterday", sessions: [] },
    { id: "previous-7-days", label: "Previous 7 Days", sessions: [] },
    { id: "earlier", label: "Earlier", sessions: [] },
  ];
  for (const session of sorted) {
    // Common Room always sorts to the top via the existing Pinned bucket —
    // it has no explicit `pinned` flag of its own (it's created once at
    // gateway boot, not pinned by the user), but must be as findable as a
    // pinned session regardless of last-activity recency (release plan 0.0a).
    if (session.pinned || session.session_type === "common_room") {
      buckets[0].sessions.push(session);
      continue;
    }
    const activity = new Date(session.last_activity).getTime();
    if (Number.isFinite(activity) && activity >= today) buckets[1].sessions.push(session);
    else if (Number.isFinite(activity) && activity >= yesterday) buckets[2].sessions.push(session);
    else if (Number.isFinite(activity) && activity >= previousSevenDays) buckets[3].sessions.push(session);
    else buckets[4].sessions.push(session);
  }
  return buckets.filter((bucket) => bucket.sessions.length > 0);
}

function matchesFilter(sessionType: string | undefined | null, filter: FilterType): boolean {
  if (filter === "all") return true;
  const type = sessionType ?? "personal";
  if (filter === "personal") return type === "personal" || type === "creative" || type === "build";
  return type === filter;
}

export function Sidebar({
  onNewSession,
  sessions,
  archivedSessions = [],
  activeSessionId,
  onSwitchSession,
  onDeleteSession,
  onRenameSession,
  onTogglePin,
  onArchiveSession,
  onRestoreSession,
  projects = [],
  projectsLoading = false,
  projectsError,
  onCreateProject,
  onRenameProject,
  onAssignSessionProject,
  pendingProjectMoveSessionIds = new Set(),
  onKeepsakeSelect,
  onOpenKeepsakesGallery,
  onOpenLibrary,
  allKeepsakes = [],
  className,
  companions,
  collapsed = false,
  onToggleCollapsed,
  collapseToggleRef,
  onCloseSidebar,
}: SidebarProps) {
  const { t } = useI18n();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState<FilterType>("all");
  const [shelf, setShelf] = useState<ShelfView>("recent");
  const [selectedProjectId, setSelectedProjectId] = useState<string | "unfiled">("unfiled");
  const [creatingProject, setCreatingProject] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [projectFormError, setProjectFormError] = useState<string | null>(null);
  const [renamingProjectId, setRenamingProjectId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState("");
  const [organizationError, setOrganizationError] = useState<string | null>(null);
  const shelfHeadingId = useId();

  // "New chat in this project": inherit the companion and room of the
  // project's most recent chat so it lands where the writer already works.
  function newChatInProject(project: Project) {
    const latest = activeSessions
      .filter((s) => s.project_id === project.id)
      .sort((a, b) => b.last_activity.localeCompare(a.last_activity))[0];
    onNewSession({
      type: (latest?.session_type ?? "personal") as NewSessionOpts["type"],
      companion: latest?.identity,
      projectId: project.id,
    });
  }

  function handleSelect(opts: NewSessionOpts) {
    setDialogOpen(false);
    onNewSession(opts);
  }

  // Session-bound wakes, slice 2′. Same overlay-exclusivity convention as
  // AppShell's openSettings — close the sidebar first, both are full-width
  // overlays on mobile.
  function wakeMeOnThis(session: Session) {
    setPendingScheduleOrigin({ sessionId: session.id, label: session.label ?? null });
    onCloseSidebar?.();
    window.location.hash = settingsHash("schedules");
  }

  // Defend the shelf boundary even if a stale caller accidentally passes a
  // mixed list. Archived conversations can never bleed into Recent.
  const activeSessions = sessions.filter((session) => !session.archived);
  const shelfSessions = shelf === "archived"
    ? archivedSessions.filter((session) => Boolean(session.archived))
    : activeSessions;
  const allCount = shelfSessions.length;
  const personalCount = shelfSessions.filter((s) => matchesFilter(s.session_type, "personal")).length;
  const writingCount = shelfSessions.filter((s) => matchesFilter(s.session_type, "writing")).length;
  const officeCount = shelfSessions.filter((s) => matchesFilter(s.session_type, "office")).length;
  const buildingCount = shelfSessions.filter((s) => matchesFilter(s.session_type, "building")).length;

  // Common Room must stay findable regardless of which room filter is active
  // (release plan 0.0a) — matchesFilter() only recognizes personal/writing/
  // office/building, so a strict filter would otherwise hide it entirely.
  // Scoped to the active (non-archived) shelf only: matchesFilter() and the
  // personal/writing/office/building counts above are untouched, and the
  // archived shelf keeps today's exact filtering (an archived Common Room,
  // if that ever happens, is not force-shown).
  const roomFilteredSessions = (
    filter === "all"
      ? shelfSessions
      : shelfSessions.filter((s) => (
          matchesFilter(s.session_type, filter)
          || (shelf !== "archived" && s.session_type === "common_room")
        ))
  );
  const filteredSessions = shelf === "projects"
    ? roomFilteredSessions.filter((session) => (
        selectedProjectId === "unfiled"
          ? session.project_id === null
          : session.project_id === selectedProjectId
      ))
    : roomFilteredSessions;
  const groupedSessions = groupSessionsByLocalDate(filteredSessions);

  const activeSessionIdentity = sessions.find((session) => session.id === activeSessionId)?.identity ?? null;
  const companionLabel = (identity: string) => (
    companions?.find((companion) => companion.id === identity)?.displayName
      ?? (FALLBACK_COMPANION.id === identity ? FALLBACK_COMPANION.displayName : identity)
  );
  const projectCount = (projectId: string | null) => activeSessions.filter((session) => (
    projectId === null ? session.project_id === null : session.project_id === projectId
  )).length;
  const selectedProject = selectedProjectId === "unfiled"
    ? null
    : projects.find((project) => project.id === selectedProjectId) ?? null;

  function beginCreateProject() {
    setProjectName("");
    setProjectFormError(null);
    setCreatingProject(true);
  }

  async function submitProject(event: FormEvent) {
    event.preventDefault();
    const creatorIdentity = companions?.some((companion) => companion.id === activeSessionIdentity)
      ? activeSessionIdentity
      : companions?.[0]?.id;
    if (!onCreateProject || !projectName.trim() || !creatorIdentity) {
      setProjectFormError("Enter a project name.");
      return;
    }
    const created = await onCreateProject(projectName, creatorIdentity);
    if (!created) {
      setProjectFormError("The project could not be created.");
      return;
    }
    setCreatingProject(false);
    setSelectedProjectId(created.id);
  }

  async function submitRename(project: Project) {
    if (!onRenameProject || !renameValue.trim()) return;
    const renamed = await onRenameProject(project.id, renameValue);
    if (renamed) {
      setRenamingProjectId(null);
      setOrganizationError(null);
    } else {
      setOrganizationError("The project could not be renamed.");
    }
  }

  async function moveSession(sessionId: string, project: Project | null) {
    if (!onAssignSessionProject) return;
    const moved = await onAssignSessionProject(sessionId, project);
    setOrganizationError(moved ? null : "The conversation could not be moved. Its previous project was restored.");
  }

  function renderConversationGroups(emptyMessage: string) {
    if (groupedSessions.length === 0) {
      return (
        <div className="session-shelf-empty" role="status">
          {emptyMessage}
        </div>
      );
    }
    return groupedSessions.map((group) => {
      const headingId = `${shelfHeadingId}-${shelf}-${group.id}`;
      return (
        <section className="session-date-group" aria-labelledby={headingId} key={group.id}>
          <h3 id={headingId}>{group.label}</h3>
          {group.sessions.map((session) => (
            <SessionItem
              key={session.id}
              session={session}
              archived={shelf === "archived"}
              active={shelf !== "archived" && session.id === activeSessionId}
              onClick={() => { if (shelf !== "archived") onSwitchSession(session.id); }}
              onDelete={() => onDeleteSession(session.id)}
              onRename={(label) => onRenameSession(session.id, label)}
              onTogglePin={() => onTogglePin(session.id)}
              onArchive={() => onArchiveSession?.(session.id)}
              onRestore={() => onRestoreSession?.(session.id)}
              companionLabel={companionLabel(session.identity)}
              projects={projects}
              onMoveToProject={shelf === "archived" ? undefined : (project) => { void moveSession(session.id, project); }}
              projectMovePending={pendingProjectMoveSessionIds.has(session.id)}
              onWakeMeOnThis={shelf === "archived" ? undefined : () => wakeMeOnThis(session)}
            />
          ))}
        </section>
      );
    });
  }

  return (
    <aside id="app-sidebar" className={`sidebar ${className || ""}`}>
      <Logo
        trailing={onToggleCollapsed && (
          <button
            ref={collapseToggleRef}
            type="button"
            className="btn btn-icon btn-ghost"
            style={{ marginLeft: "auto" }}
            aria-expanded={!collapsed}
            aria-controls="app-sidebar"
            aria-label={collapsed ? t("sidebar.expand") : t("sidebar.collapse")}
            onClick={onToggleCollapsed}
          >
            <PanelLeftClose size={16} />
          </button>
        )}
      />

      <button className="btn btn-primary" style={{ width: "100%" }} onClick={() => setDialogOpen(true)}>
        <Plus size={16} />
        {t("sidebar.newSession")}
      </button>

      {onOpenLibrary && (
        <button className="sidebar-library-button" onClick={onOpenLibrary}>
          <LibraryBig size={16} />
          <span>Library</span>
          <span className="sidebar-library-button__hint">Browse</span>
        </button>
      )}

      <div className="session-filters">
        <div
          className={`filter-item${filter === "all" ? " active" : ""}`}
          onClick={() => setFilter("all")}
        >
          <MessageSquare size={16} />
          <span>{t("sidebar.allSessions")}</span>
          <span className="filter-count">{allCount}</span>
        </div>
        <div
          className={`filter-item${filter === "personal" ? " active" : ""}`}
          onClick={() => setFilter("personal")}
        >
          <Heart size={16} />
          <span>{t("session.personal" as TranslationKey)}</span>
          <span className="filter-count">{personalCount}</span>
        </div>
        <div
          className={`filter-item${filter === "writing" ? " active" : ""}`}
          onClick={() => setFilter("writing")}
        >
          <BookOpen size={16} />
          <span>{t("session.writers-room")}</span>
          <span className="filter-count">{writingCount}</span>
        </div>
        <div
          className={`filter-item${filter === "office" ? " active" : ""}`}
          onClick={() => setFilter("office")}
        >
          <Briefcase size={16} />
          <span>{t("session.office" as TranslationKey)}</span>
          <span className="filter-count">{officeCount}</span>
        </div>
        <div
          className={`filter-item${filter === "building" ? " active" : ""}`}
          onClick={() => setFilter("building")}
        >
          <Wrench size={16} />
          <span>{t("session.building")}</span>
          <span className="filter-count">{buildingCount}</span>
        </div>
      </div>

      <div className="recent-sessions">
        <div className="session-shelf-switch" role="tablist" aria-label="Conversation shelves">
          <button role="tab" aria-selected={shelf === "recent"} className={shelf === "recent" ? "active" : ""} onClick={() => setShelf("recent")}>
            Recent <span>{sessions.filter((session) => !session.archived).length}</span>
          </button>
          <button role="tab" aria-selected={shelf === "projects"} className={shelf === "projects" ? "active" : ""} onClick={() => setShelf("projects")}>
            Projects <span>{projects.length}</span>
          </button>
          <button role="tab" aria-selected={shelf === "archived"} className={shelf === "archived" ? "active" : ""} onClick={() => setShelf("archived")}>
            Archived <span>{archivedSessions.filter((session) => Boolean(session.archived)).length}</span>
          </button>
        </div>
        <div className="label session-shelf-label" id={shelfHeadingId}>
          {shelf === "recent"
            ? t("sidebar.recentSessions")
            : shelf === "projects"
              ? "Project conversations"
              : "Archived conversations"}
        </div>

        {shelf === "projects" && (
          <div className="project-shelf">
            <div className="project-shelf-actions">
              <button className="project-create-button" onClick={beginCreateProject} disabled={!onCreateProject || !companions?.length}>
                <Plus size={14} /> New project
              </button>
            </div>

            {creatingProject && (
              <form className="project-form" onSubmit={submitProject}>
                <label>
                  <span>Project name</span>
                  <input
                    autoFocus
                    value={projectName}
                    onChange={(event) => setProjectName(event.target.value)}
                    maxLength={200}
                    placeholder="The work this holds"
                  />
                </label>
                {projectFormError && <div className="project-inline-error" role="alert">{projectFormError}</div>}
                <div className="project-form-actions">
                  <button type="submit">Create</button>
                  <button type="button" onClick={() => setCreatingProject(false)}>Cancel</button>
                </div>
              </form>
            )}

            {projectsLoading ? (
              <div className="project-status" role="status">Loading projects…</div>
            ) : projectsError ? (
              <div className="project-inline-error" role="alert">{projectsError}</div>
            ) : (
              <div className="project-list" aria-label="Projects">
                {projects.map((project) => (
                  <div className={`project-row${selectedProjectId === project.id ? " active" : ""}`} key={project.id}>
                    {renamingProjectId === project.id ? (
                      <div className="project-rename-form">
                        <input
                          aria-label={`Rename ${project.name}`}
                          value={renameValue}
                          onChange={(event) => setRenameValue(event.target.value)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter") void submitRename(project);
                            if (event.key === "Escape") setRenamingProjectId(null);
                          }}
                          maxLength={200}
                          autoFocus
                        />
                        <button aria-label={`Save ${project.name}`} onClick={() => { void submitRename(project); }}>Save</button>
                        <button aria-label={`Cancel renaming ${project.name}`} onClick={() => setRenamingProjectId(null)}><X size={13} /></button>
                      </div>
                    ) : (
                      <>
                        <button
                          className="project-select-button"
                          aria-pressed={selectedProjectId === project.id}
                          aria-label={`${project.name}, ${projectCount(project.id)} conversations`}
                          onClick={() => setSelectedProjectId(project.id)}
                        >
                          <FolderKanban size={14} />
                          <span className="project-row-name">{project.name}</span>
                          <span className="project-count" aria-label={`${projectCount(project.id)} conversations`}>{projectCount(project.id)}</span>
                        </button>
                        <button
                          className="project-rename-button"
                          aria-label={`Rename ${project.name}`}
                          onClick={() => { setRenamingProjectId(project.id); setRenameValue(project.name); }}
                        >
                          <Pencil size={13} />
                        </button>
                      </>
                    )}
                  </div>
                ))}
                <button
                  className={`project-select-button project-unfiled${selectedProjectId === "unfiled" ? " active" : ""}`}
                  aria-pressed={selectedProjectId === "unfiled"}
                  aria-label={`Unfiled, ${projectCount(null)} conversations`}
                  onClick={() => setSelectedProjectId("unfiled")}
                >
                  <MessageSquare size={14} />
                  <span className="project-row-name">Unfiled</span>
                  <span className="project-count" aria-label={`${projectCount(null)} conversations`}>{projectCount(null)}</span>
                </button>
                {projects.length === 0 && <div className="project-status">No projects yet. Unfiled conversations stay right here.</div>}
              </div>
            )}
            {organizationError && <div className="project-inline-error" role="alert">{organizationError}</div>}
            <div className="project-selection-label">
              {selectedProject ? selectedProject.name : "Unfiled"}
            </div>
            {selectedProject && (
              <button
                type="button"
                className="project-create-button project-new-chat-button"
                aria-label={`New chat in ${selectedProject.name}`}
                onClick={() => newChatInProject(selectedProject)}
              >
                <Plus size={14} /> New chat
              </button>
            )}
          </div>
        )}

        {renderConversationGroups(
          shelf === "recent"
            ? t("sidebar.noSessions")
            : shelf === "projects"
              ? selectedProjectId === "unfiled"
                ? "No unfiled conversations in this room."
                : "No active conversations in this project and room."
              : "No archived conversations.",
        )}
      </div>

      {onKeepsakeSelect && (
        <KeepsakesList
          keepsakes={allKeepsakes}
          onSelect={onKeepsakeSelect}
          onOpenGallery={onOpenKeepsakesGallery}
        />
      )}

      <NewSessionDialog
        open={dialogOpen}
        onSelect={handleSelect}
        onClose={() => setDialogOpen(false)}
        companions={companions}
      />
    </aside>
  );
}
